import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Chip, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Stack, useTheme, IconButton, Divider
} from '@mui/material';
import { Close as CloseIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

interface DocumentField { label: string; labelAr: string; value?: string; valueBefore?: string; valueAfter?: string; hasChange: boolean; }
interface DocumentLine { lineNumber: number; itemCode?: string; itemName?: string; itemNameAr?: string; quantity?: number; unitCost?: number; totalCost?: number; debit?: number; credit?: number; accountName?: string; description?: string; }
interface DocumentPreview { documentType: string; entityId: number; documentNumber: string; title: string; titleAr: string; date?: string; postedDate?: string; postedBy?: string; status?: string; fields: DocumentField[]; lines: DocumentLine[]; navigationPath?: string; }

interface Props {
  open: boolean;
  documentType: string | null;
  entityId: number | null;
  onClose: () => void;
  isRTL: boolean;
}

const DocumentPreviewModal: React.FC<Props> = ({ open, documentType, entityId, onClose, isRTL }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState<DocumentPreview | null>(null);

  useEffect(() => {
    if (open && documentType && entityId) {
      setLoading(true); setDoc(null);
      axiosInstance.get<DocumentPreview>(`/InventoryHealthHub/document/${documentType}/${entityId}`)
        .then(r => setDoc(r.data))
        .catch(err => toast.error(err.response?.data?.message || 'Failed to load document'))
        .finally(() => setLoading(false));
    }
  }, [open, documentType, entityId]);

  const statusColor = (s?: string) => {
    if (!s) return 'default';
    if (s === 'Posted' || s === 'Approved' || s === 'Received') return 'success';
    if (s === 'Draft' || s === 'Pending') return 'warning';
    if (s === 'Cancelled' || s === 'Rejected') return 'error';
    return 'info';
  };

  const isJE = doc?.documentType === 'JournalEntry';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          📄 {doc ? (isRTL ? doc.titleAr : doc.title) : (isRTL ? 'جارٍ التحميل...' : 'Loading...')}
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 300 }}>
        {loading && <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>}
        {doc && !loading && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
              <Chip label={doc.documentType} color="primary" size="small" />
              {doc.status && <Chip label={doc.status} color={statusColor(doc.status) as any} size="small" />}
              {doc.date && <Typography variant="body2">{isRTL ? '📅 التاريخ:' : '📅 Date:'} {new Date(doc.date).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</Typography>}
              {doc.postedDate && <Typography variant="body2">{isRTL ? '✅ رحّل:' : '✅ Posted:'} {new Date(doc.postedDate).toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</Typography>}
            </Box>

            {doc.fields.length > 0 && (
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>{isRTL ? '📋 الحقول' : '📋 Fields'}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                  {doc.fields.map((f, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', p: 1, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                      <Typography variant="body2" color="text.secondary">{isRTL ? f.labelAr : f.label}</Typography>
                      <Typography variant="body2" fontWeight={500}>{f.value || '—'}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            {doc.lines.length > 0 && (
              <Paper elevation={1} sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 1.5, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: '8px 8px 0 0' }}>
                  <Typography variant="subtitle2" fontWeight="bold">{isRTL ? '📑 السطور' : '📑 Lines'} ({doc.lines.length})</Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        {isJE ? (
                          <>
                            <TableCell>{isRTL ? 'الحساب' : 'Account'}</TableCell>
                            <TableCell align="right">{isRTL ? 'مدين' : 'Debit'}</TableCell>
                            <TableCell align="right">{isRTL ? 'دائن' : 'Credit'}</TableCell>
                            <TableCell>{isRTL ? 'الوصف' : 'Description'}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                            <TableCell align="right">{isRTL ? 'الكمية' : 'Qty'}</TableCell>
                            <TableCell align="right">{isRTL ? 'التكلفة' : 'Cost'}</TableCell>
                            <TableCell align="right">{isRTL ? 'الإجمالي' : 'Total'}</TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {doc.lines.map(l => (
                        <TableRow key={l.lineNumber}>
                          <TableCell>{l.lineNumber}</TableCell>
                          {isJE ? (
                            <>
                              <TableCell>{l.accountName}</TableCell>
                              <TableCell align="right" sx={{ color: '#1976d2' }}>{l.debit && l.debit > 0 ? l.debit.toFixed(4) : '—'}</TableCell>
                              <TableCell align="right" sx={{ color: '#c62828' }}>{l.credit && l.credit > 0 ? l.credit.toFixed(4) : '—'}</TableCell>
                              <TableCell>{l.description}</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>{l.itemCode} — {isRTL ? l.itemNameAr : l.itemName}</TableCell>
                              <TableCell align="right">{l.quantity?.toFixed(4)}</TableCell>
                              <TableCell align="right">{l.unitCost?.toFixed(4)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{l.totalCost?.toFixed(4)}</TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {doc?.navigationPath && (
          <Button startIcon={<OpenIcon />} variant="outlined" onClick={() => { window.open(doc.navigationPath, '_blank'); }}>
            {isRTL ? 'فتح في تبويب جديد' : 'Open in new tab'}
          </Button>
        )}
        <Button onClick={onClose} variant="contained">{isRTL ? 'إغلاق' : 'Close'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewModal;
